/*
  Vibe Video List — WonderSkull custom (not part of stock Shrine PRO).

  Two jobs, both scoped to this section's own markup (never touches Shrine's slider or
  the CDN bundle):

  1. Play facade + auto-reset — each card renders a thumbnail + a Play button only.
     Nothing heavy loads until the visitor clicks Play: a Shopify-hosted <video>
     (preload="none") starts, or a YouTube <iframe> is injected on demand. When
     playback ends the card returns to its facade (mp4 via the 'ended' event; YouTube
     via the IFrame API postMessage 'onStateChange' → ended). Whether the control bar
     shows is driven by the section's data-vvl-controls flag.

  2. Carousel arrows — end state is read from the scroll position (authoritative), so
     the prev arrow hides at the start and the next arrow at the end without an
     IntersectionObserver threshold that could miss a card settled a hair short.
*/
(function () {
  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Accepts youtube.com/watch?v=, youtu.be/, /embed/, /shorts/ — returns an embed URL
  // with autoplay, the JS API (needed to detect the end and reset), and the control bar
  // toggled to match the section setting. Falls back to the raw value if no 11-char id
  // is found (lets an already-formed embed URL through, still appending our params).
  function youtubeEmbed(url, controls) {
    var m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
    var base = m ? 'https://www.youtube.com/embed/' + m[1] : url;
    var sep = base.indexOf('?') === -1 ? '?' : '&';
    return base + sep + 'autoplay=1&rel=0&playsinline=1&enablejsapi=1&controls=' + (controls ? '1' : '0');
  }

  function resetMedia(media) {
    if (!media) return;
    media.classList.remove('vvl__media--playing');
    var teaser = media.querySelector('[data-vvl-thumb]');
    if (teaser && isOnScreen(teaser)) { var tp = teaser.play(); if (tp && tp.catch) tp.catch(function () {}); }
    var iframe = media.querySelector('.vvl__iframe');
    if (iframe) iframe.remove();
    var video = media.querySelector('.vvl__video');
    if (video) {
      video.pause();
      video.removeAttribute('controls');
      video.currentTime = 0;
      video.load(); // restore the poster frame
    }
  }

  function initPlay(root) {
    var controls = root.getAttribute('data-vvl-controls') !== 'false';

    root.querySelectorAll('[data-vvl-play]').forEach(function (btn) {
      if (btn.dataset.vvlPlayReady === 'true') return;
      btn.dataset.vvlPlayReady = 'true';

      btn.addEventListener('click', function () {
        var media = btn.closest('.vvl__media');
        if (!media) return;
        var video = media.querySelector('.vvl__video');
        var embed = btn.getAttribute('data-vvl-embed');

        if (video) {
          media.classList.add('vvl__media--playing');
          if (controls) video.setAttribute('controls', '');
          video.muted = false;
          video.addEventListener('ended', function () { resetMedia(media); }, { once: true });
          var p = video.play();
          if (p && p.catch) p.catch(function () {});
        } else if (embed) {
          var iframe = document.createElement('iframe');
          iframe.className = 'vvl__iframe';
          iframe.src = youtubeEmbed(embed, controls);
          iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
          iframe.setAttribute('allowfullscreen', '');
          iframe.setAttribute('title', btn.getAttribute('aria-label') || 'Video');
          // Once loaded, subscribe to the player's state events so we can reset on end.
          iframe.addEventListener('load', function () {
            try { iframe.contentWindow.postMessage('{"event":"listening"}', '*'); } catch (e) {}
          });
          media.classList.add('vvl__media--playing');
          media.appendChild(iframe);
        }
      });
    });
  }

  // Single global listener for all YouTube players: on "ended" (info === 0), find the
  // iframe that sent the message and reset its card.
  var youtubeListenerBound = false;
  function bindYoutubeEndListener() {
    if (youtubeListenerBound) return;
    youtubeListenerBound = true;
    window.addEventListener('message', function (event) {
      if (typeof event.data !== 'string' || event.origin.indexOf('youtube.com') === -1) return;
      var data;
      try { data = JSON.parse(event.data); } catch (e) { return; }
      if (!data || data.event !== 'onStateChange' || data.info !== 0) return;
      document.querySelectorAll('.vvl__iframe').forEach(function (iframe) {
        if (iframe.contentWindow === event.source) resetMedia(iframe.closest('.vvl__media'));
      });
    });
  }

  function initArrows(root) {
    if (root.dataset.vvlArrowsReady === 'true') return;

    var track = root.querySelector('.vvl__track');
    var prev = root.querySelector('[data-vvl-prev]');
    var next = root.querySelector('[data-vvl-next]');
    if (!track || !prev || !next) return;

    root.dataset.vvlArrowsReady = 'true';

    function scrollByPage(direction) {
      track.scrollBy({
        left: track.clientWidth * 0.8 * direction,
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth'
      });
    }

    prev.addEventListener('click', function () { scrollByPage(-1); });
    next.addEventListener('click', function () { scrollByPage(1); });

    function updateEnds() {
      var max = track.scrollWidth - track.clientWidth;
      var x = track.scrollLeft;
      root.classList.toggle('vvl--at-start', x <= 2 || max <= 0);
      root.classList.toggle('vvl--at-end', x >= max - 2 || max <= 0);
    }

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(function () { ticking = false; updateEnds(); });
      }
    }, { passive: true });
    window.addEventListener('resize', updateEnds, { passive: true });
    track.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', updateEnds, { once: true });
    });
    updateEnds();
  }

  // ---- Looping teaser posters -------------------------------------------
  // A row of autoplaying clips is the one genuinely battery-expensive thing
  // here, so each plays only while its card is on screen. Visitors who ask
  // for reduced motion get the poster frame and no motion at all.
  function isOnScreen(el) {
    var r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < (window.innerHeight || 0);
  }

  function initTeasers(scope) {
    if (REDUCED_MOTION.matches) return;
    var clips = (scope || document).querySelectorAll('[data-vvl-thumb]');
    if (!clips.length || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        if (v.closest('.vvl__media--playing')) return; // real video has the stage
        if (entry.isIntersecting) {
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          v.pause();
        }
      });
    }, { threshold: 0.25 });
    clips.forEach(function (v) { io.observe(v); });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('.vvl').forEach(function (root) {
      initPlay(root);
      initArrows(root);
      initTeasers(root);
    });
  }

  bindYoutubeEndListener();
  initAll();

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
